import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import dayjs from "dayjs"
import { Meterpoint } from "./meterpoint"
import { IMeterpoint, MeterpointType } from "./meterpoint.types"
import { MeterParser, IMeterRaw } from "../meter/meter.parser"
import type { AccountMeterpointsStructure } from "@/api/backofficeRest/generated/model"

export type IMeterpointRaw = AccountMeterpointsStructure

function parseMeterpointType(input: string): MeterpointType {
  const normalized = input.trim().toLowerCase()

  switch (normalized) {
    case "mpan":
      return "MPAN"
    case "mprn":
      return "MPRN"
    default:
      // Tolerate unmapped upstream values like the Ark parser did — throwing
      // here would blank every meterpoint on the account over one bad type.
      return input as MeterpointType
  }
}

// The backoffice upper-cases these enum-ish values where Ark sent title case
// (CREDIT vs Credit); restore the original casing the UI displayed, keeping
// acronym values intact.
const DISPLAY_CASING_ACRONYMS = new Set(["DCC"])

function parseDisplayCasing(value: string): string {
  if (value !== value?.toUpperCase()) return value
  if (DISPLAY_CASING_ACRONYMS.has(value)) return value
  return value.charAt(0) + value.slice(1).toLowerCase()
}

export const MeterpointParser: IParser<IMeterpoint, IMeterpointRaw> = {
  toDomain: (data: IMeterpointRaw) => {
    try {
      return new Meterpoint(
        data.id,
        data.identifier,
        parseMeterpointType(data.meterpointType),
        data.supplyStatus,
        dayjs(data.supplyStartDate),
        parseDisplayCasing(data.operationType),
        parseDisplayCasing(data.serviceType),
        data.meters
          .filter((meter): meter is IMeterRaw => meter !== null)
          .map((meter) => MeterParser.toDomain(meter)),
        data.gspGroup ?? undefined,
        data.profileClass ?? undefined,
        data.dno ?? undefined,
        data.dcc ?? undefined,
        data.mam ?? undefined,
        data.mop ?? undefined,
        data.dataAvailable ?? true,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
